import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, RefreshCw, Download, Upload, Send, Search, FileCheck2, MessageCircle, Star, Check, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { formatNaira, formatDate } from "@/lib/format";

const STATUSES = [
  "PENDING PAYMENT",
  "PAID",
  "PROCESSING",
  "RESERVATION CREATED",
  "DELIVERED",
  "CHANGE REQUESTED",
  "COMPLETED",
  "CANCELLED",
];

const statusColor = (s) => {
  switch (s) {
    case "PAID": return "bg-primary/10 text-primary";
    case "PENDING PAYMENT": return "bg-amber-100 text-amber-700";
    case "DELIVERED":
    case "COMPLETED": return "bg-[#0F4C3A]/10 text-[#0F4C3A]";
    case "CANCELLED": return "bg-destructive/10 text-destructive";
    default: return "bg-secondary text-foreground";
  }
};

const SERVICE_HOURS = { standard: 24, express: 4, priority: 1 };
const DONE_STATUSES = ["PENDING PAYMENT", "DELIVERED", "COMPLETED", "CANCELLED"];

const isOverdue = (o) => {
  if (DONE_STATUSES.includes(o.status)) return false;
  const base = o.paid_at || o.created_at;
  if (!base) return false;
  const hours = SERVICE_HOURS[o.service] ?? 24;
  return Date.now() > new Date(base).getTime() + hours * 3600 * 1000;
};

const dueLabel = (o) => {
  const base = o.paid_at || o.created_at;
  if (!base) return "—";
  const hours = SERVICE_HOURS[o.service] ?? 24;
  return new Date(new Date(base).getTime() + hours * 3600 * 1000).toLocaleString("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

const fmtDT = (v) => (v ? new Date(v).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const waDeliveryLink = (o) => {
  if (!o || !o.reservation_token) return null;
  const num = String(o.whatsapp || "").replace(/[^0-9]/g, "");
  if (!num) return null;
  const link = `${BACKEND}/api/orders/${o.id}/reservation?token=${o.reservation_token}`;
  const msg = `Hello ${o.customer_name}, your VisaFlight confirmed airline reservation for order ${o.order_number} is ready. Download it here: ${link}`;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0, revenue: 0 });
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [uploading, setUploading] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState("PENDING");

  const loadReviews = useCallback(async (status = "PENDING") => {
    try {
      const params = status && status !== "ALL" ? { status } : {};
      const { data } = await api.get("/admin/reviews", { params });
      setReviews(data);
    } catch {
      /* handled by orders 401 flow */
    }
  }, []);

  const moderate = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      toast.success(status === "APPROVED" ? "Review approved and now visible." : "Review hidden.");
      loadReviews(reviewFilter);
    } catch {
      toast.error("Could not update review.");
    }
  };

  const removeReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted.");
      loadReviews(reviewFilter);
    } catch {
      toast.error("Could not delete review.");
    }
  };

  const load = useCallback(async (opts = {}) => {
    const { search: sq = "", status = "ALL" } = opts;
    setLoading(true);
    try {
      const params = {};
      if (sq) params.search = sq;
      if (status && status !== "ALL") params.status = status;
      const [o, s] = await Promise.all([
        api.get("/admin/orders", { params }),
        api.get("/admin/stats"),
      ]);
      setOrders(o.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("vf_admin_token");
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Could not load orders.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("vf_admin_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
    loadReviews("PENDING");
  }, [load, navigate, loadReviews]);

  useEffect(() => {
    if (!localStorage.getItem("vf_admin_token")) return;
    loadReviews(reviewFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFilter]);

  useEffect(() => {
    if (!localStorage.getItem("vf_admin_token")) return;
    const t = setTimeout(() => load({ search, status: statusFilter }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "visaflight_orders.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export orders.");
    }
  };

  const uploadReservation = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !active) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/orders/${active.id}/reservation`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Refetch so the secure token (used for the WhatsApp/download link) is available.
      try {
        const { data: fresh } = await api.get(`/admin/orders/${active.id}`);
        setActive(fresh);
        setOrders((prev) => prev.map((o) => (o.id === fresh.id ? fresh : o)));
      } catch {
        setActive((a) => ({ ...a, reservation_filename: data.filename }));
      }
      toast.success("Reservation file uploaded.");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Upload failed."));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deliver = async () => {
    if (!active) return;
    setDelivering(true);
    try {
      const { data } = await api.post(`/admin/orders/${active.id}/deliver`, { mark_delivered: true });
      if (data.status === "email_failed") {
        toast.warning("File is ready but the email could not be sent. Share the link on WhatsApp.");
      } else {
        toast.success("Reservation emailed to the customer.");
      }
      setActive({ ...data.order });
      setOrders((prev) => prev.map((o) => (o.id === data.order.id ? data.order : o)));
      load({ search, status: statusFilter });
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not send reservation."));
    } finally {
      setDelivering(false);
    }
  };

  const addNote = async () => {
    if (!active || !noteText.trim()) return;
    setAddingNote(true);
    try {
      const { data } = await api.post(`/admin/orders/${active.id}/notes`, { note: noteText.trim() });
      setActive({ ...data });
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      setNoteText("");
      toast.success("Note added.");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not add note."));
    } finally {
      setAddingNote(false);
    }
  };

  const shareWhatsApp = async (e) => {
    if (!waDeliveryLink(active)) {
      e.preventDefault();
      toast.error("Upload a reservation file first.");
      return;
    }
    try {
      const { data } = await api.post(`/admin/orders/${active.id}/notes`, { note: "Reservation link shared via WhatsApp" });
      setActive((a) => ({ ...a, history: data.history }));
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
    } catch {
      /* non-fatal — link still opens */
    }
  };

  const logout = () => {
    localStorage.removeItem("vf_admin_token");
    navigate("/admin/login", { replace: true });
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${active.id}`, {
        status: active.status,
        reservation_status: active.reservation_status,
        pnr: active.pnr,
        delivery_status: active.delivery_status,
        internal_notes: active.internal_notes,
        change_count: parseInt(active.change_count, 10) || 0,
      });
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      toast.success("Order updated.");
      setActive(null);
      load({ search, status: statusFilter });
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not update order."));
    } finally {
      setSaving(false);
    }
  };

  const StatCard = ({ label, value }) => (
    <div className="rounded-sm border border-border bg-card p-5">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-2xl font-bold text-foreground">{value}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <span className="font-heading text-lg font-extrabold tracking-tight text-foreground">
            VISA<span className="text-primary">FLIGHT</span> <span className="text-sm font-medium text-muted-foreground">Admin</span>
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-btn" className="rounded-sm"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
            <Button variant="outline" size="sm" onClick={() => load({ search, status: statusFilter })} data-testid="refresh-btn" className="rounded-sm"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn" className="rounded-sm"><LogOut className="mr-2 h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Total orders" value={stats.total} />
          <StatCard label="Paid orders" value={stats.paid} />
          <StatCard label="Pending payment" value={stats.pending} />
          <StatCard label="Revenue" value={formatNaira(stats.revenue)} />
        </div>

        <div className="mt-8 rounded-sm border border-border bg-card">
          <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-heading text-lg font-semibold text-foreground">Orders</h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="orders-search"
                  placeholder="Search order, name, email, destination…"
                  className="w-full rounded-sm pl-8 sm:w-72"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="w-full rounded-sm sm:w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : orders.length === 0 ? (
            <p className="px-5 py-16 text-center text-muted-foreground">No orders yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="orders-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id} data-testid={`order-row-${o.order_number}`}>
                      <TableCell className="font-medium text-foreground">{o.order_number}</TableCell>
                      <TableCell>{o.customer_name}</TableCell>
                      <TableCell>{o.destination}</TableCell>
                      <TableCell className="capitalize">{o.service}</TableCell>
                      <TableCell>{formatNaira(o.amount)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge className={`rounded-sm border-0 font-medium ${statusColor(o.status)}`}>{o.status}</Badge>
                          {isOverdue(o) && (
                            <Badge data-testid={`overdue-${o.order_number}`} className="rounded-sm border-0 bg-destructive/10 font-medium text-destructive">Overdue</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(o.created_at)}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" data-testid={`manage-${o.order_number}`} onClick={() => setActive({ ...o })} className="rounded-sm">Manage</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Reviews moderation */}
        <div className="mt-8 rounded-sm border border-border bg-card">
          <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-heading text-lg font-semibold text-foreground">Customer reviews</h2>
            <Select value={reviewFilter} onValueChange={setReviewFilter}>
              <SelectTrigger data-testid="review-filter" className="w-full rounded-sm sm:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="HIDDEN">Hidden</SelectItem>
                <SelectItem value="ALL">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {reviews.length === 0 ? (
            <p className="px-5 py-12 text-center text-muted-foreground">No reviews in this view.</p>
          ) : (
            <div className="divide-y divide-border">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`review-item-${r.id}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-border"}`} />
                        ))}
                      </div>
                      <Badge className={`rounded-sm border-0 text-xs ${r.status === "APPROVED" ? "bg-[#0F4C3A]/10 text-[#0F4C3A]" : r.status === "HIDDEN" ? "bg-secondary text-muted-foreground" : "bg-amber-100 text-amber-700"}`}>{r.status}</Badge>
                    </div>
                    <p className="mt-2 text-sm text-foreground">“{r.message}”</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {r.name}{r.location ? ` · ${r.location}` : ""}{r.order_number ? ` · ${r.order_number}` : ""} · {formatDate(r.created_at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {r.status !== "APPROVED" && (
                      <Button size="sm" variant="outline" data-testid={`approve-${r.id}`} onClick={() => moderate(r.id, "APPROVED")} className="rounded-sm text-[#0F4C3A]"><Check className="mr-1 h-4 w-4" /> Approve</Button>
                    )}
                    {r.status !== "HIDDEN" && (
                      <Button size="sm" variant="outline" data-testid={`hide-${r.id}`} onClick={() => moderate(r.id, "HIDDEN")} className="rounded-sm"><EyeOff className="mr-1 h-4 w-4" /> Hide</Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`delete-review-${r.id}`} onClick={() => removeReview(r.id)} className="rounded-sm text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Order {active.order_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-sm border border-border bg-secondary p-4 text-sm">
                  <p><span className="text-muted-foreground">Customer:</span> {active.customer_name} · {active.email} · {active.whatsapp}</p>
                  <p className="mt-1"><span className="text-muted-foreground">Passenger:</span> {active.passenger_name} ({active.num_passengers})</p>
                  <p className="mt-1"><span className="text-muted-foreground">Route:</span> {active.departure} → {active.destination}</p>
                  <p className="mt-1"><span className="text-muted-foreground">Dates:</span> {formatDate(active.departure_date)} {active.return_date ? `→ ${formatDate(active.return_date)}` : ""}</p>
                  <p className="mt-1"><span className="text-muted-foreground">Visa:</span> {active.visa_type} — {active.visa_country}</p>
                  <p className="mt-1"><span className="text-muted-foreground">Paystack ref:</span> {active.paystack_reference || "—"}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Status</Label>
                    <Select value={active.status} onValueChange={(v) => setActive((a) => ({ ...a, status: v }))}>
                      <SelectTrigger data-testid="edit-status" className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Delivery status</Label>
                    <Input className="rounded-sm" value={active.delivery_status || ""} onChange={(e) => setActive((a) => ({ ...a, delivery_status: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>PNR / reference</Label>
                    <Input data-testid="edit-pnr" className="rounded-sm" value={active.pnr || ""} onChange={(e) => setActive((a) => ({ ...a, pnr: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Reservation status</Label>
                    <Input className="rounded-sm" value={active.reservation_status || ""} onChange={(e) => setActive((a) => ({ ...a, reservation_status: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Change count</Label>
                    <Input type="number" min="0" className="rounded-sm" value={active.change_count ?? 0} onChange={(e) => setActive((a) => ({ ...a, change_count: e.target.value }))} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Internal notes</Label>
                  <Textarea data-testid="edit-notes" className="rounded-sm" rows={3} value={active.internal_notes || ""} onChange={(e) => setActive((a) => ({ ...a, internal_notes: e.target.value }))} />
                </div>

                <div className="rounded-sm border border-border p-4">
                  <div className="flex items-center gap-2">
                    <FileCheck2 className="h-4 w-4 text-primary" />
                    <span className="font-heading text-sm font-semibold text-foreground">Deliver reservation</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Upload the confirmed reservation (PDF, PNG or JPG), then email it to the customer.</p>
                  {active.reservation_filename && (
                    <p className="mt-2 text-xs font-medium text-[#0F4C3A]">Uploaded: {active.reservation_filename}</p>
                  )}
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-sm border border-input px-3 py-2 text-sm font-medium hover:border-primary hover:text-primary">
                      <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload file"}
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" data-testid="reservation-file" onChange={uploadReservation} disabled={uploading} />
                    </label>
                    <Button
                      onClick={deliver}
                      disabled={delivering || !active.reservation_filename}
                      data-testid="deliver-btn"
                      className="rounded-sm bg-[#0F4C3A] font-semibold text-white hover:bg-[#0c3d2e]"
                    >
                      {delivering ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Send via email</>}
                    </Button>
                    <a
                      href={waDeliveryLink(active) || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="deliver-whatsapp-btn"
                      onClick={shareWhatsApp}
                      aria-disabled={!waDeliveryLink(active)}
                      className={`inline-flex items-center justify-center gap-2 rounded-sm px-4 py-2 text-sm font-semibold text-white ${waDeliveryLink(active) ? "bg-[#25D366] hover:bg-[#1eb356]" : "pointer-events-none bg-[#25D366]/40"}`}
                    >
                      <MessageCircle className="h-4 w-4" /> Send via WhatsApp
                    </a>
                  </div>
                </div>

                <div className="rounded-sm border border-border p-4">
                  <span className="font-heading text-sm font-semibold text-foreground">Timeline &amp; notes</span>
                  <div className="mt-2 flex gap-2">
                    <Input
                      data-testid="note-input"
                      placeholder="Add a note (e.g. contacted customer)…"
                      className="rounded-sm"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addNote()}
                    />
                    <Button onClick={addNote} disabled={addingNote || !noteText.trim()} data-testid="add-note-btn" variant="outline" className="rounded-sm">
                      {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                    </Button>
                  </div>
                  <ol className="mt-4 space-y-3 border-l border-border pl-4">
                    {[...(active.history || [])].reverse().map((h, i) => (
                      <li key={i} className="relative text-sm" data-testid="timeline-entry">
                        <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                        <p className="text-foreground">{h.text}</p>
                        <p className="text-xs text-muted-foreground">{fmtDT(h.at)}</p>
                      </li>
                    ))}
                    {(!active.history || active.history.length === 0) && (
                      <li className="text-sm text-muted-foreground">No history yet.</li>
                    )}
                  </ol>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActive(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="save-order-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#003DB3]">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
